import {Component, Host, h, Prop, State, Build} from '@stencil/core';
import {
  ArrowLeftEndOnRectangleIcon,
  ArrowRightEndOnRectangleIcon,
  ViewfinderCircleIcon,
  ArrowDownOnSquareIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from './heroicons';

const PING = "https://pingchat.ai"; //http://localhost:8000";


@Component({
  tag: 'ping-chat',
  styleUrl: 'ping-chat.css',
  shadow: true,
})
export class ScrivChat {

  /**
   * The team slug to connect to.
   */
  @Prop() team!: string;

  /**
   * The bot slug to connect to.
   */
  @Prop() bot!: string;

  /**
   * The text to display on the button.
   */
  @Prop() buttonText: string = "Chat";

  /**
   * Whether the chat widget is visible on load.
   */
  @Prop({ mutable: true }) visible: boolean = false;

  /**
   * The initial position of the chat widget on the screen.
   */
  @Prop({ mutable: true }) position: 'left' | 'center' | 'right' = 'right';

  /**
   * Whether the chat widget is initially expanded.
   */
  @Prop({ mutable: true }) expanded: boolean = false;

  @Prop() ping: string = PING;

  // once set this will stay true so that the iframe doesn't reload
  @State() loaded: boolean = false;

  componentWillLoad() {
    this.loaded = this.visible;
    if (!Build.isDev) {
      // don't allow setting different Ping url in production
      this.ping = PING;
    }
  }

  toggle() {
    this.visible = !this.visible;
    this.loaded = true;
  }

  setPosition(position: 'left' | 'center' | 'right') {
    if (position === this.position) return;
    this.position = position;
  }

  toggleSize() {
    this.expanded = !this.expanded;
  }

  getPositionClasses() {
    const baseClasses = `fixed w-full sm:w-[450px] ${this.expanded ? 'h-5/6' : 'h-3/5'} bg-white border border-gray-200 shadow-lg rounded-lg overflow-hidden flex flex-col`;

    const positionClasses = {
      left: 'left-0 sm:left-5 bottom-0 sm:bottom-5',
      right: 'right-0 sm:right-5 bottom-0 sm:bottom-5',
      center: 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
    }[this.position];

    return `${baseClasses} ${positionClasses}`;
  }


  render() {
    return (
      <Host>
        <button class="btn" onClick={() => this.toggle()}>{this.buttonText}</button>
        {this.visible && (
          <div class={this.getPositionClasses()}>
            <div class="flex justify-between items-center px-2 py-2 border-b border-gray-100">
              <div class="flex gap-1">
                <button
                  class={{
                    'hidden sm:block': true,
                    'p-1.5 rounded-md transition-colors duration-200 hover:bg-gray-100': true,
                    'text-blue-600': this.position === 'left',
                    'text-gray-500': this.position !== 'left'
                  }}
                  onClick={() => this.setPosition('left')}
                  aria-label="Dock to left"
                  title="Dock to left"
                >
                  <ArrowLeftEndOnRectangleIcon/>
                </button>
                <button
                  class={{
                    'p-1.5 rounded-md transition-colors duration-200 hover:bg-gray-100': true,
                    'text-blue-600': this.position === 'center',
                    'text-gray-500': this.position !== 'center'
                  }}
                  onClick={() => this.setPosition('center')}
                  aria-label="Center"
                  title="Center"
                >
                  <ViewfinderCircleIcon/>
                </button>
                <button
                  class={{
                    'sm:hidden': true,
                    'p-1.5 rounded-md transition-colors duration-200 hover:bg-gray-100': true,
                    'text-blue-600': this.position === 'right',
                    'text-gray-500': this.position !== 'right'
                  }}
                  onClick={() => this.setPosition('right')}
                  aria-label="Bottom"
                  title="Bottom"
                >
                  <ArrowDownOnSquareIcon/>
                </button>
                <button
                  class={{
                    'hidden sm:block': true,
                    'p-1.5 rounded-md transition-colors duration-200 hover:bg-gray-100': true,
                    'text-blue-600': this.position === 'right',
                    'text-gray-500': this.position !== 'right'
                  }}
                  onClick={() => this.setPosition('right')}
                  aria-label="Dock to right"
                  title="Dock to right"
                >
                  <ArrowRightEndOnRectangleIcon/>
                </button>
              </div>
              <div class="flex gap-1">
                <button
                  class="p-1.5 rounded-md transition-colors duration-200 hover:bg-gray-100 text-gray-500"
                  onClick={() => this.toggleSize()}
                  aria-label={this.expanded ? "Collapse" : "Expand"}
                  title={this.expanded ? "Collapse" : "Expand"}
                >
                  {this.expanded ? <ChevronDownIcon/> : <ChevronUpIcon/>}
                </button>
                <button
                  class="p-1.5 hover:bg-gray-100 rounded-md transition-colors duration-200 text-gray-500"
                  onClick={() => this.visible = false}
                  aria-label="Close"
                >
                  <XMarkIcon/>
                </button>
              </div>
            </div>
            {this.loaded && (
              <iframe class="w-full flex-grow iframe-placeholder"
                      src={`${this.ping}/a/${this.team}/bots/b/${this.bot}/chat/`}></iframe>
            )}
            <p class="font-sans text-center mt-4 text-sm text-gray-700">⚡ Lavet med <a class="link" href="https://pingchat.ai">Pingchat.ai</a>
            </p>
          </div>
        )}
      </Host>
    );
  }
}
